import React, { useState, useRef, useEffect } from 'react';
import { makeStyles, Theme } from '@material-ui/core/styles';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    position: 'relative',
    width: '100%',
  },
  triggerWrapper: {
    position: 'relative',
    width: '100%',
  },
  input: {
    width: '100%',
    height: '44px',
    border: '1px solid #E2E5ED',
    borderRadius: '4px',
    padding: '10px 36px 10px 12px',
    backgroundColor: '#FFFFFF',
    fontSize: '14px',
    fontFamily: "'Poppins', sans-serif",
    color: '#324054',
    boxSizing: 'border-box',
    outline: 'none',
  },
  overlayLabel: {
    position: 'absolute',
    left: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    color: '#324054',
    fontSize: '14px',
    fontFamily: "'Poppins', sans-serif",
    pointerEvents: 'none',
  },
  chevron: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  },
  clear: {
    position: 'absolute',
    right: '32px',
    top: '50%',
    transform: 'translateY(-50%)',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
  },
  chipContainer: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    height: '44px',
    border: '1px solid #E2E5ED',
    borderRadius: '4px',
    padding: '0 12px',
    backgroundColor: '#FFFFFF',
    boxSizing: 'border-box',
    cursor: 'pointer',
    position: 'relative',
  },
  chip: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 12px',
    border: '1px solid #324054',
    borderRadius: '20px',
    backgroundColor: '#FFFFFF',
    fontSize: '14px',
    fontFamily: "'Poppins', sans-serif",
    color: '#324054',
    fontWeight: 500,
    maxHeight: '28px',
    boxSizing: 'border-box',
    marginRight: '0px',
    marginLeft: '0px',
  },
  chipDelete: {
    display: 'inline-flex',
    alignItems: 'center',
    marginLeft: '8px',
    cursor: 'pointer',
  },
  inputField: {
    border: 'none',
    outline: 'none',
    height: '100%',
    flex: 1,
    minWidth: '60px',
    fontSize: '14px',
    fontFamily: "'Poppins', sans-serif",
    color: '#324054',
    backgroundColor: 'transparent',
    padding: 0,
  },
  dropdown: {
    position: 'absolute',
    top: '48px',
    left: 0,
    width: '100%',
    backgroundColor: '#FFFFFF',
    border: '1px solid #E2E5ED',
    borderRadius: '4px',
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.08)',
    zIndex: 1100,
    display: 'flex',
    flexDirection: 'column',
    boxSizing: 'border-box',
    overflow: 'hidden',
    alignItems: 'stretch',
  },
  header: {
    padding: '12px',
    borderBottom: '1px solid #E2E5ED',
    backgroundColor: '#FFFFFF',
    boxSizing: 'border-box',
    width: '100%',
  },
  addBtn: {
    backgroundColor: '#2a7bec',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '5px',
    padding: '10px 14px',
    fontSize: '14px',
    fontFamily: "'Poppins', sans-serif",
    fontWeight: 500,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    width: '100%',
    boxSizing: 'border-box',
  },
  inlineWrapper: {
    display: 'flex',
    gap: '8px',
    width: '100%',
    boxSizing: 'border-box',
  },
  inlineInput: {
    flex: 1,
    border: '1px solid #E2E5ED',
    borderRadius: '4px',
    padding: '8px 12px',
    fontSize: '14px',
    fontFamily: "'Poppins', sans-serif",
    color: '#324054',
    outline: 'none',
    height: '38px',
    boxSizing: 'border-box',
  },
  inlineAddBtn: {
    backgroundColor: '#2a7bec',
    color: '#FFFFFF',
    border: 'none',
    borderRadius: '5px',
    padding: '0 16px',
    fontSize: '14px',
    fontFamily: "'Poppins', sans-serif",
    cursor: 'pointer',
    height: '38px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
  },
  optionsList: {
    maxHeight: '200px',
    overflowY: 'auto',
    width: '100%',
    boxSizing: 'border-box',
  },
  optionItem: {
    padding: '12px 16px',
    fontSize: '14px',
    fontFamily: "'Poppins', sans-serif",
    color: '#324054',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'center',
    width: '100%',
    boxSizing: 'border-box',
    backgroundColor: '#FFFFFF',
    '&:hover': {
      backgroundColor: '#F5F7FA',
    },
    '&.selected': {
      backgroundColor: '#F5F7FA',
    },
  },
  noOptions: {
    padding: '16px',
    color: '#868DAA',
    fontSize: '14px',
    textAlign: 'center',
  },
  chipTextWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flex: 1,
    overflow: 'hidden',
  },
  chipText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

interface AddableSelectProps {
  id?: string;
  value: string;
  options: string[];
  placeholder: string;
  onAddOption: (val: string) => void;
  onChange: (val: string) => void;
  addButtonLabel: string;
  inputPlaceholder: string;
}

export function AddableSelect({
  id,
  value,
  options,
  placeholder,
  onAddOption,
  onChange,
  addButtonLabel,
  inputPlaceholder
}: AddableSelectProps) {
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [newItemText, setNewItemText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setIsAdding(false);
        setNewItemText('');
        setError(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const openDropdown = () => {
    setIsOpen(true);
  };

  const handleSelect = (opt: string) => {
    onChange(opt);
    setSearchTerm('');
    setIsOpen(false);
  };

  const performAddNewItem = () => {
    const trimmed = newItemText.trim();
    if (!trimmed) {
      setError('Value is required');
      return;
    }
    const exists = options.some(opt => opt.toLowerCase() === trimmed.toLowerCase());
    if (exists) {
      setError('Value already exists');
      return;
    }
    onAddOption(trimmed);
    onChange(trimmed);
    setNewItemText('');
    setError(null);
    setIsAdding(false);
    setIsOpen(false);
  };

  const handleAddNewItem = (e: React.MouseEvent) => {
    e.stopPropagation();
    performAddNewItem();
  };

  const displayValue = isOpen ? searchTerm : (value || '');
  const filteredOptions = options.filter(opt =>
    opt.toLowerCase().includes((isOpen ? searchTerm : '').toLowerCase())
  );

  return (
    <div ref={containerRef} className={`${classes.container} addable-select-container`}>
      <div
        className={`${classes.chipContainer} addable-select-trigger-wrapper`}
        onClick={openDropdown}
        style={{
          ...(error ? { borderColor: '#EF5350' } : {}),
          paddingRight: '36px'
        }}
      >
        <div className={classes.chipTextWrapper}>
          {value && (
            <div className={`${classes.chip} addable-select-chip`}>
              <span className={classes.chipText}>
                {value}
              </span>
              <span
                className={`${classes.chipDelete} addable-select-chip-delete`}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange('');
                  setSearchTerm('');
                  setError(null);
                }}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#EF5350"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="15" y1="9" x2="9" y2="15"></line>
                  <line x1="9" y1="9" x2="15" y2="15"></line>
                </svg>
              </span>
            </div>
          )}
          {(!value || isOpen) && (
            <input
              id={id}
              ref={inputRef}
              type="text"
              className={`${classes.inputField} addable-select-input-field`}
              value={isOpen ? searchTerm : ''}
              onChange={(e) => {
                if (!isOpen) {
                  openDropdown();
                }
                setSearchTerm(e.target.value);
                if (e.target.value === '') {
                  onChange('');
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Backspace' && !searchTerm && value) {
                  onChange('');
                  setError(null);
                }
              }}
              onFocus={openDropdown}
              placeholder={value ? '' : placeholder}
              autoComplete="off"
            />
          )}
        </div>
        <span
          className={`${classes.chevron} addable-select-chevron`}
          onClick={(e) => {
            e.stopPropagation();
            if (isOpen) {
              setIsOpen(false);
              setIsAdding(false);
              setNewItemText('');
              setError(null);
            } else {
              setSearchTerm('');
              openDropdown();
            }
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
          >
            <path
              d="M6 9l6 6 6-6"
              stroke="#868DAA"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </div>

      {isOpen && (
        <div className={`${classes.dropdown} addable-select-dropdown`}>
          <div className={`${classes.header} addable-select-header`}>
            {!isAdding ? (
              <button
                type="button"
                className={`${classes.addBtn} addable-select-add-btn`}
                onClick={(e) => {
                  e.stopPropagation();
                  setIsAdding(true);
                  setError(null);
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <path d="M12 5v14M5 12h14" stroke="#FFFFFF" strokeWidth="3" strokeLinecap="round"/>
                </svg>
                {addButtonLabel}
              </button>
            ) : (
              <div>
                <div className={`${classes.inlineWrapper} addable-select-inline-wrapper`}>
                  <input
                    type="text"
                    placeholder={inputPlaceholder}
                    value={newItemText}
                    onChange={(e) => {
                      setNewItemText(e.target.value);
                      setError(null);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        e.stopPropagation();
                        performAddNewItem();
                      }
                    }}
                    onClick={(e) => e.stopPropagation()}
                    className={`${classes.inlineInput} addable-select-inline-input`}
                    style={error ? { borderColor: '#EF5350' } : {}}
                  />
                  <button
                    type="button"
                    onClick={handleAddNewItem}
                    className={`${classes.inlineAddBtn} addable-select-inline-add-btn`}
                  >
                    Add
                  </button>
                </div>
                {error && (
                  <span className="form-error" style={{ marginTop: '4px' }}>
                    {error}
                  </span>
                )}
              </div>
            )}
          </div>

          <div className={`${classes.optionsList} addable-select-options-list`}>
            {filteredOptions.map((opt, i) => (
              <div
                key={i}
                onMouseDown={() => handleSelect(opt)}
                className={`${classes.optionItem} addable-select-option-item ${opt === value ? 'selected' : ''}`}
              >
                <span>{opt}</span>
              </div>
            ))}
            {filteredOptions.length === 0 && (
              <div className={`${classes.noOptions} addable-select-no-options`}>
                No options found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
